import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../stores/auth'
import { Button, Modal } from './ui'

export const IDLE_LIMIT_MS = 15 * 60_000
export const WARN_BEFORE_MS = 5 * 60_000

const ACTIVITY_EVENTS = ['pointermove', 'pointerdown', 'keydown', 'wheel'] as const
const THROTTLE_MS = 1_000
const TICK_MS = 1_000

/**
 * Idle-timeout guard, mounted inside the authenticated shell. After
 * IDLE_LIMIT_MS without user input it signs out exactly like the sidebar ⏻
 * button (store logout → /login; the persisted auth store propagates it to
 * other tabs). With WARN_BEFORE_MS left, a countdown modal offers "Stay
 * logged in". The clock is wall-clock based (Date.now() deltas, never
 * accumulated ticks), so a hidden tab whose timers sleep still times out on
 * schedule.
 */
export function IdleTimeout() {
  const logout = useAuthStore((s) => s.logout)
  const navigate = useNavigate()
  const lastActivityRef = useRef(Date.now())
  const lastEventRef = useRef(0)
  const [remainingMs, setRemainingMs] = useState(IDLE_LIMIT_MS)

  useEffect(() => {
    const onActivity = () => {
      const now = Date.now()
      // Throttled to ~1 reset/sec; script timers never reach this handler.
      if (now - lastEventRef.current < THROTTLE_MS) return
      lastEventRef.current = now
      lastActivityRef.current = now
    }
    for (const event of ACTIVITY_EVENTS) document.addEventListener(event, onActivity, { passive: true })
    const tick = window.setInterval(() => {
      setRemainingMs(IDLE_LIMIT_MS - (Date.now() - lastActivityRef.current))
    }, TICK_MS)
    return () => {
      for (const event of ACTIVITY_EVENTS) document.removeEventListener(event, onActivity)
      window.clearInterval(tick)
    }
  }, [])

  useEffect(() => {
    if (remainingMs <= 0) {
      logout()
      navigate('/login')
    }
  }, [remainingMs, logout, navigate])

  const stayLoggedIn = () => {
    const now = Date.now()
    lastEventRef.current = now
    lastActivityRef.current = now
    setRemainingMs(IDLE_LIMIT_MS)
  }

  const warning = remainingMs > 0 && remainingMs <= WARN_BEFORE_MS
  const seconds = Math.ceil(remainingMs / 1000)
  const countdown = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`

  return (
    <Modal open={warning} title="Still there?" onClose={stayLoggedIn}>
      <div className="text-sm text-ostrel-ink">
        <p>
          You've been inactive for a while. You'll be signed out in{' '}
          <strong className="tnum font-mono text-ostrel-danger">{countdown}</strong>.
        </p>
      </div>
      <div className="mt-4 flex justify-end">
        <Button variant="primary" size="sm" onClick={stayLoggedIn} autoFocus>
          Stay logged in
        </Button>
      </div>
    </Modal>
  )
}
